/**
 * Equipment — module replacing General Affairs' Excel equipment
 * register (Registro Attrezzatura+inventario.xlsx).
 *
 * Sub-views:
 *   Book              — the 3-step wizard (EquipmentWizard.tsx)
 *   My requests       — own bookings: edit purpose/notes/return date,
 *                       propose a hand-over (EquipmentTransfer.tsx),
 *                       confirm or dispute the return report
 *                       (EquipmentReturn.tsx), cancel while PENDING/APPROVED
 *   How it works      — read-only guide (EquipmentGuide.tsx)
 *   Requests          — admin queue: approve (with per-line reductions),
 *                       reject with reason
 *   Pickups & returns — confirm physical pickup (who collected, which
 *                       supervisor handed over) and issue return reports
 *                       (per line: returned / damaged / lost / out of
 *                       service) that the holder must confirm; overdue
 *                       highlighted
 *   Availability      — live availability for any date window
 *   Inventory         — the inventory master: quantities, out-of-service
 *                       units, notes; deactivate instead of delete
 *   Archive           — stored return-report PDFs: ZIP download + purge
 *
 * Data is fetched on tab open and after every action (the Directory
 * pattern) — no realtime subscription; equipment changes are low-
 * frequency and always follow an explicit action here.
 *
 * Every status transition is validated against BOOKING_TRANSITIONS and
 * audited into equipment_events. Server-side, RLS restricts the whole
 * module to admins and the create RPC re-validates availability under
 * row locks (see supabase/21_equipment.sql).
 */
import type { JSX } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { state, isDemoMode } from "../state/store";
import { displayName, bySurname, activePeople } from "../utils/names";
import { restGet, restPatch, restUpsert, restRpc, restDelete, invokeFunction, storageDownload, storageDelete } from "../utils/withRetry";
import { formatBytes } from "../utils/imageCompress";
import { toast } from "../utils/toast";
import { isWeekend } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import { EquipmentWizard } from "./EquipmentWizard";
import { EquipmentGuide } from "./EquipmentGuide";
import { TransferBanner } from "./EquipmentTransfer";
import { MyRequests, lineSummary } from "./EquipmentMine";
import { ReturnReportPanel, ReturnModal, ArchiveTab, renderReportPdf } from "./EquipmentReturn";
import { STATUS_CLS, nowIso, backdropHandlers, PersonSelect, saveBytes, openBytes, fmtDate } from "./equipmentShared";
import {
  computeAvailability,
  canTransition,
  returnOutcome,
  isOverdue,
  nextStep,
  notificationState,
  selfRosterName,
  deleteWarning,
  dateProblem,
  timeSlots,
  returnReportFileName,
  BOOKING_STATUS_LABEL,
  statusLabel,
  AVAILABILITY_STATUS_META,
  NOTIFICATION_AUDIENCE,
  type BookingStatus,
  type EquipmentBooking,
  type EquipmentBookingLine,
  type EquipmentCategory,
  type EquipmentItem,
  type EquipmentNotification,
  type EquipmentDocument,
  type EquipmentTransfer,
  type EquipmentReturnReport,
  type NotificationState,
} from "../utils/equipment";

/** Top level: requester tabs + a read-only guide for everyone, and one
 *  "Manage" tab for admins with its own sub-navigation (keeps the bar
 *  short for staff). */
type SubTab = "book" | "mine" | "guide" | "manage";
type ManageTab = "queue" | "moves" | "availability" | "inventory" | "archive" | "notifications";

/** Synthetic register for localhost demo mode: the real inventory master
 *  plus a handful of bookings covering every lifecycle status (incl. an
 *  overdue one) so all sub-views render meaningfully. */
function demoEquipment(): {
  categories: EquipmentCategory[];
  items: EquipmentItem[];
  bookings: EquipmentBooking[];
  lines: EquipmentBookingLine[];
  notifications: EquipmentNotification[];
  transfers: EquipmentTransfer[];
  reports: EquipmentReturnReport[];
  docs: EquipmentDocument[];
} {
  const categories: EquipmentCategory[] = [
    { id: 1, name: "Trasferte", sort_order: 1 },
    { id: 2, name: "Podcast", sort_order: 2 },
  ];
  const mk = (id: number, category_id: number, name: string, total_qty: number, extra?: Partial<EquipmentItem>): EquipmentItem => ({
    id, category_id, name, description: "", total_qty, lost_qty: 0,
    out_of_service_qty: 0, unit_notes: "", is_active: true, ...extra,
  });
  const items: EquipmentItem[] = [
    mk(1, 1, "Cellulare Samsung", 3),
    mk(2, 1, "Cavalletti Manfrotto", 2),
    mk(3, 1, "Cavalletti Joby", 3),
    mk(4, 1, "Microfoni Boya", 5, { lost_qty: 1, out_of_service_qty: 1, unit_notes: "1 cappuccio mancante" }),
    mk(5, 1, "Adattatori Samsung", 6),
    mk(6, 1, "Adattatori iPhone", 2),
    mk(7, 2, "Registratore audio compatto a 32 bit", 2, { out_of_service_qty: 1 }),
    mk(8, 2, "Pacchetto accessori", 4),
    mk(9, 2, "Filtro antivento in pelliccia WSU-1", 3),
    mk(10, 2, "Custodia imbottita", 3, { is_active: false }),
  ];
  const today = new Date();
  const day = (offset: number): string => {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const mkB = (id: number, status: BookingStatus, pickup: string, ret: string, over?: Partial<EquipmentBooking>): EquipmentBooking => ({
    id, reference: `ATT-DEMO-${String(id).padStart(4, "0")}`,
    requester_id: null,
    requester_name: state.value.currentUser ?? "Demo Person",
    on_behalf_of: "", pickup_delegate: "", pickup_date: pickup, pickup_time: "09:30",
    return_date: ret, return_time: "", purpose: "", requester_notes: "",
    status, decided_by: "", decided_at: null, decision_reason: "",
    collected_at: null, collected_by: "", pickup_supervisor: "",
    returned_at: null, returned_by: "", return_supervisor: "",
    admin_notes: "", created_at: nowIso(), ...over,
  });
  const bookings: EquipmentBooking[] = [
    mkB(1, "PENDING", day(3), day(5), { purpose: "Video interviews — demo event" }),
    mkB(2, "APPROVED", day(1), day(2), { decided_by: "Demo Admin", decision_reason: "" }),
    mkB(3, "COLLECTED", day(-6), day(-2), { collected_by: "Demo Person", pickup_supervisor: "Demo Admin" }),
    mkB(4, "RETURNED", day(-12), day(-10), { returned_by: "Demo Person", return_supervisor: "Demo Admin", returned_at: nowIso() }),
    mkB(5, "REJECTED", day(2), day(4), { decision_reason: "Kit already reserved for the fair" }),
    mkB(6, "RETURN_PENDING", day(-4), day(-1), { collected_by: "Demo Person", pickup_supervisor: "Demo Admin", returned_by: state.value.currentUser ?? "Demo Person", return_supervisor: "Demo Admin", returned_at: nowIso(), purpose: "Podcast recording — demo" }),
  ];
  const mkL = (id: number, booking_id: number, item_id: number, req: number, over?: Partial<EquipmentBookingLine>): EquipmentBookingLine => ({
    id, booking_id, item_id, qty_requested: req, qty_approved: null,
    qty_returned: 0, qty_damaged: 0, damage_note: "", qty_lost: 0, line_notes: "", ...over,
  });
  const lines: EquipmentBookingLine[] = [
    mkL(1, 1, 1, 1), mkL(2, 1, 4, 2),
    mkL(3, 2, 2, 1, { qty_approved: 1 }),
    mkL(4, 3, 7, 1, { qty_approved: 1 }),
    mkL(5, 3, 8, 2, { qty_approved: 2 }),
    mkL(6, 4, 4, 2, { qty_approved: 2, qty_returned: 1, qty_damaged: 1, damage_note: "spugna rovinata", qty_lost: 1 }),
    mkL(7, 5, 5, 3),
    mkL(8, 6, 7, 1, { qty_approved: 1, qty_returned: 1, qty_damaged: 1, damage_note: "display graffiato" }),
    mkL(9, 6, 9, 2, { qty_approved: 2, qty_returned: 2 }),
  ];
  const mkN = (id: number, booking_id: number, kind: EquipmentNotification["kind"], over?: Partial<EquipmentNotification>): EquipmentNotification => ({
    id, booking_id, kind, created_at: nowIso(), claimed_at: null, sent_at: null,
    attempts: 0, last_error: null, recipients: [], resend_id: null, payload: null, ...over,
  });
  const notifications: EquipmentNotification[] = [
    mkN(4, 1, "created", { sent_at: nowIso(), attempts: 1, recipients: ["comms.admin@icons.it"], resend_id: "demo" }),
    mkN(3, 2, "approved", { attempts: 2, last_error: "Resend 429: rate limited", claimed_at: nowIso() }),
    mkN(2, 5, "rejected", { sent_at: nowIso(), attempts: 1, recipients: ["demo.person@icons.it"], resend_id: "demo" }),
    mkN(1, 3, "collected", { sent_at: nowIso(), attempts: 1, last_error: "No recipient — requester has no email on profile or roster" }),
    mkN(5, 6, "return_report_issued", { sent_at: nowIso(), attempts: 1, recipients: ["demo.person@icons.it"], resend_id: "demo" }),
  ];
  // A hand-over of the collected kit, proposed by the demo user to the
  // first other active colleague; the admin side is still to approve.
  const me = state.value.currentUser ?? "Demo Person";
  const colleague = activePeople(state.value.roster).find((n) => n !== me) ?? "Demo Colleague";
  const transfers: EquipmentTransfer[] = [{
    id: 1, booking_id: 3, from_name: me, from_profile_id: null, to_name: colleague, to_profile_id: "demo-colleague",
    proposed_by: me, proposed_at: nowIso(), accepted_at: nowIso(), approved_at: null, approved_by: "",
    status: "PENDING", closed_at: null, closed_by: "", close_reason: "",
  }];
  const reports: EquipmentReturnReport[] = [
    {
      id: 1, booking_id: 6, version: 1, status: "ISSUED",
      lines: [
        { line_id: 8, item_id: 7, item_name: "Registratore audio compatto a 32 bit", due: 1, returned: 1, lost: 0, damaged: 1, out_of_service: 0, damage_note: "display graffiato" },
        { line_id: 9, item_id: 9, item_name: "Filtro antivento in pelliccia WSU-1", due: 2, returned: 2, lost: 0, damaged: 0, out_of_service: 0, damage_note: "" },
      ],
      returned_by: me, received_by: "Demo Admin", admin_notes: "Please handle the recorder with its case next time.",
      declared_by: "Demo Admin", declared_at: nowIso(), confirmed_by: "", confirmed_at: null,
      closed_without_confirmation: false, close_note: "", disputed_at: null, dispute_note: "",
    },
    {
      id: 2, booking_id: 4, version: 1, status: "CONFIRMED",
      lines: [{ line_id: 6, item_id: 4, item_name: "Microfoni Boya", due: 2, returned: 1, lost: 1, damaged: 1, out_of_service: 0, damage_note: "spugna rovinata" }],
      returned_by: "Demo Person", received_by: "Demo Admin", admin_notes: "",
      declared_by: "Demo Admin", declared_at: `${day(-10)}T15:04:00.000Z`, confirmed_by: "Demo Person", confirmed_at: `${day(-10)}T16:30:00.000Z`,
      closed_without_confirmation: false, close_note: "", disputed_at: null, dispute_note: "",
    },
  ];
  const docs: EquipmentDocument[] = [{
    id: 1, booking_id: 4, kind: "return_report", path: `bookings/4/demo-return_report.pdf`,
    file_name: `ATT-DEMO-0004_${day(-10)}_Demo-Person_return-report.pdf`, content_type: "application/pdf",
    size_bytes: 21_480, uploaded_by: "Demo Person", created_at: nowIso(), archived_at: null, archived_by: "",
  }];
  return { categories, items, bookings, lines, notifications, transfers, reports, docs };
}

export function Equipment(): JSX.Element {
  const s = state.value;
  // The real person, not the admins' virtual "Administrator" identity.
  const me = selfRosterName(s);
  const isAdmin = s.isAdmin;
  const [sub, setSub] = useState<SubTab>("book");
  const [categories, setCategories] = useState<EquipmentCategory[]>([]);
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [bookings, setBookings] = useState<EquipmentBooking[]>([]);
  const [lines, setLines] = useState<EquipmentBookingLine[]>([]);
  const [notifications, setNotifications] = useState<EquipmentNotification[]>([]);
  const [docs, setDocs] = useState<EquipmentDocument[]>([]);
  const [transfers, setTransfers] = useState<EquipmentTransfer[]>([]);
  const [reports, setReports] = useState<EquipmentReturnReport[]>([]);
  const [notifError, setNotifError] = useState<string | null>(null);
  const [notifBusy, setNotifBusy] = useState(false);
  const [manageTab, setManageTab] = useState<ManageTab>("queue");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    // Localhost demo mode has no Supabase session — seed a synthetic
    // register so every sub-view is previewable (same philosophy as the
    // demo calendar). Writes still surface their normal error toast.
    if (isDemoMode.value) {
      const demo = demoEquipment();
      setCategories(demo.categories);
      setItems(demo.items);
      setBookings(demo.bookings);
      setLines(demo.lines);
      setNotifications(demo.notifications);
      setTransfers(demo.transfers);
      setReports(demo.reports);
      setDocs(demo.docs);
      setLoading(false);
      return;
    }
    try {
      const [cats, its, bks, lns] = await Promise.all([
        restGet<EquipmentCategory>("equipment_categories", "select=*&order=sort_order"),
        restGet<EquipmentItem>("equipment_items", "select=*&order=name"),
        restGet<EquipmentBooking>("equipment_bookings", "select=*&order=pickup_date.desc&limit=500"),
        restGet<EquipmentBookingLine>("equipment_booking_lines", "select=*"),
      ]);
      setCategories(cats);
      setItems(its);
      setBookings(bks);
      setLines(lns);
      setLoadError(null);
      // Documents, hand-overs and return reports: RLS returns all rows to
      // admins, own rows to users. Each tolerates a DB that hasn't got its
      // migration yet (26 / 28 / 29) by degrading to an empty list.
      try {
        setDocs(await restGet<EquipmentDocument>("equipment_booking_documents", "select=*&order=id.desc"));
      } catch {
        setDocs([]);
      }
      try {
        setTransfers(await restGet<EquipmentTransfer>("equipment_transfers", "select=*&order=id.desc&limit=300"));
      } catch {
        setTransfers([]);
      }
      try {
        setReports(await restGet<EquipmentReturnReport>("equipment_return_reports", "select=*"));
      } catch {
        setReports([]);
      }
      // Outbox is admin-readable only; fetched separately so a DB that
      // hasn't got migration 23 yet degrades to an explained empty panel
      // instead of breaking the whole module.
      if (state.value.isAdmin) {
        try {
          setNotifications(
            await restGet<EquipmentNotification>(
              "equipment_notifications",
              "select=*&order=id.desc&limit=60",
            ),
          );
          setNotifError(null);
        } catch (err) {
          setNotifications([]);
          setNotifError(
            `Couldn't read the notification outbox (${err instanceof Error ? err.message : "error"}) — has migration 23_equipment_notifications.sql been applied?`,
          );
        }
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load equipment data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Belt-and-braces drain of the email outbox right after an action. The
  // Database Webhook already does this server-side the instant a row is
  // queued; this nudge covers a webhook that isn't configured yet. A
  // failure here is logged, never shown — the Notifications panel under
  // Manage is the source of truth for delivery. Claiming is atomic on
  // the server, so the two triggers can never double-send.
  const nudgeNotify = (): void => {
    if (isDemoMode.value) return;
    invokeFunction("equipment-notify").catch((err: unknown) => {
      console.warn("equipment-notify nudge failed:", err);
    });
  };
  const afterAction = async (): Promise<void> => {
    nudgeNotify();
    await load();
  };

  const sendPending = async (): Promise<void> => {
    if (isDemoMode.value) {
      toast("Demo mode — emails aren't sent.");
      return;
    }
    setNotifBusy(true);
    try {
      const r = await invokeFunction<{ claimed: number; sent: number; skipped: number; failed: number }>(
        "equipment-notify",
      );
      toast(`Notifications: ${r.sent} sent · ${r.skipped} skipped · ${r.failed} failed`);
    } catch (err) {
      toast(
        err instanceof Error
          ? `Notifier unreachable — ${err.message}`
          : "Notifier unreachable — is the equipment-notify function deployed?",
      );
    } finally {
      setNotifBusy(false);
      await load();
    }
  };

  const retryFailed = async (): Promise<void> => {
    if (isDemoMode.value) {
      toast("Demo mode — emails aren't sent.");
      return;
    }
    try {
      const rows = await restRpc<number>("equipment_reset_failed_notifications", {});
      const n = rows[0] ?? 0;
      toast(`${n} notification${n === 1 ? "" : "s"} queued for retry`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Couldn't reset failed notifications");
      return;
    }
    await sendPending();
  };

  const linesFor = (bookingId: number): EquipmentBookingLine[] =>
    lines.filter((l) => l.booking_id === bookingId);
  const docsFor = (bookingId: number): EquipmentDocument[] =>
    docs.filter((d) => d.booking_id === bookingId);

  /** The one open hand-over on a booking, if any. */
  const transferFor = (bookingId: number): EquipmentTransfer | null =>
    transfers.find((t) => t.booking_id === bookingId && t.status === "PENDING") ?? null;
  const reportFor = (bookingId: number): EquipmentReturnReport | null =>
    reports.find((r) => r.booking_id === bookingId) ?? null;

  /* ---- documents: stored return reports (and legacy signed sheets) ---- */
  const deleteDoc = async (d: EquipmentDocument): Promise<void> => {
    if (!isAdmin) return;
    if (isDemoMode.value) { toast("Demo mode — nothing to delete."); return; }
    if (!confirm(`Delete "${d.file_name}" permanently?`)) return;
    try {
      await storageDelete("equipment-docs", [d.path]);
      await restDelete("equipment_booking_documents", `id=eq.${d.id}`);
    } catch (err) {
      toast(err instanceof Error ? `Delete failed — ${err.message}` : "Delete failed");
      return;
    }
    await logEvent({ booking_id: d.booking_id, event_type: "document_deleted", note: d.file_name });
    toast("Document deleted");
    await load();
  };

  /* ---- retention: purge history older than 24 months (admin) ---- */
  const retentionCutoff = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 24);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const purgeable = useMemo(
    () => bookings.filter(
      (b) => (b.status === "RETURNED" || b.status === "REJECTED" || b.status === "CANCELLED") && b.return_date < retentionCutoff,
    ),
    [bookings, retentionCutoff],
  );
  const liveDocs = useMemo(() => docs.filter((d) => !d.archived_at), [docs]);
  const purgeOld = async (): Promise<void> => {
    if (!isAdmin || purgeable.length === 0) return;
    if (isDemoMode.value) { toast("Demo mode — nothing is purged."); return; }
    const ids = purgeable.map((b) => b.id);
    const paths = liveDocs.filter((d) => ids.includes(d.booking_id)).map((d) => d.path);
    if (!confirm(
      `Permanently delete ${ids.length} booking${ids.length === 1 ? "" : "s"} older than 24 months (returned / rejected / cancelled) and ${paths.length} stored document${paths.length === 1 ? "" : "s"}?\n\nItem stock and lost counts are unaffected. This cannot be undone.`,
    )) {
      return;
    }
    try {
      for (let i = 0; i < paths.length; i += 100) await storageDelete("equipment-docs", paths.slice(i, i + 100));
      for (let i = 0; i < ids.length; i += 100) await restDelete("equipment_bookings", `id=in.(${ids.slice(i, i + 100).join(",")})`);
    } catch (err) {
      toast(err instanceof Error ? `Purge stopped — ${err.message}` : "Purge failed");
      await load();
      return;
    }
    await logEvent({ event_type: "purged", note: `Purged ${ids.length} bookings before ${retentionCutoff} and ${paths.length} documents` });
    toast(`Purged ${ids.length} old booking${ids.length === 1 ? "" : "s"}`);
    await load();
  };
  const docBytes = liveDocs.reduce((a, d) => a + d.size_bytes, 0);
  const downloadDoc = async (d: EquipmentDocument): Promise<void> => {
    if (isDemoMode.value) { toast("Demo mode — no files to download."); return; }
    try {
      const blob = await storageDownload("equipment-docs", d.path);
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) toast("Your browser blocked the new tab — allow pop-ups for this site.");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast(err instanceof Error ? `Download failed — ${err.message}` : "Download failed");
    }
  };
  /** Render a report PDF on the fly (no stored file needed). */
  const renderPdf = async (b: EquipmentBooking, r: EquipmentReturnReport, mode: "open" | "save"): Promise<void> => {
    try {
      const bytes = await renderReportPdf(b, r);
      if (mode === "save" || !openBytes(bytes, "application/pdf")) saveBytes(bytes, returnReportFileName(b, r), "application/pdf");
    } catch (err) {
      toast(err instanceof Error ? `Couldn't render the PDF — ${err.message}` : "Couldn't render the PDF");
    }
  };

  /** Everything that hangs under a booking card: documents, an open
   *  hand-over, the return report awaiting confirmation. Same block for
   *  holders and admins — the role decides which buttons appear. */
  const renderExtra = (b: EquipmentBooking, onAmend?: (b: EquipmentBooking) => void): JSX.Element => {
    const t = transferFor(b.id);
    const r = reportFor(b.id);
    return (
      <>
        <DocsStrip
          booking={b}
          docs={docsFor(b.id)}
          report={r}
          canDelete={isAdmin}
          onDownload={(d) => void downloadDoc(d)}
          onDelete={(d) => void deleteDoc(d)}
          onRenderPdf={r ? () => void renderPdf(b, r, "save") : undefined}
        />
        {t && <TransferBanner transfer={t} me={me} profileId={s.profileId} isAdmin={isAdmin} onChanged={afterAction} />}
        {r && b.status === "RETURN_PENDING" && (
          <ReturnReportPanel
            booking={b}
            report={r}
            me={me}
            profileId={s.profileId}
            isAdmin={isAdmin}
            onChanged={afterAction}
            onAmend={onAmend ? () => onAmend(b) : undefined}
          />
        )}
      </>
    );
  };

  /** Append an audit row. Best-effort: an audit hiccup must not block
   *  the operation the admin just performed (the booking row itself
   *  still records the outcome + actor). */
  const logEvent = async (row: Record<string, unknown>): Promise<void> => {
    try {
      await restUpsert("equipment_events", [{ actor: displayName(me), ...row }]);
    } catch (err) {
      console.warn("equipment_events insert failed:", err);
    }
  };

  /** Shared guard for every status change. */
  const transition = async (
    b: EquipmentBooking,
    to: BookingStatus,
    patch: Record<string, unknown>,
    eventNote = "",
  ): Promise<boolean> => {
    if (!canTransition(b.status, to)) {
      toast(`Can't move a ${BOOKING_STATUS_LABEL[b.status]} booking to ${BOOKING_STATUS_LABEL[to]}.`);
      return false;
    }
    try {
      await restPatch("equipment_bookings", `id=eq.${b.id}`, {
        status: to,
        updated_at: nowIso(),
        ...patch,
      });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Update failed");
      return false;
    }
    await logEvent({
      booking_id: b.id,
      event_type: to.toLowerCase(),
      from_status: b.status,
      to_status: to,
      note: eventNote,
    });
    return true;
  };

  const cancelBooking = async (b: EquipmentBooking): Promise<void> => {
    if (!canTransition(b.status, "CANCELLED")) {
      toast(`A ${BOOKING_STATUS_LABEL[b.status]} booking can't be cancelled.`);
      return;
    }
    if (!confirm(`Cancel booking ${b.reference}?`)) return;
    // Goes through the SECURITY DEFINER RPC (migration 22): users can
    // cancel their OWN pending/approved bookings without table-level
    // write rights; the audit row is written server-side.
    try {
      await restRpc("equipment_cancel_booking", { p_booking_id: b.id });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Cancellation failed");
      return;
    }
    toast(`${b.reference} cancelled`);
    await afterAction();
  };

  /** Admin hard-delete — any status. Mistakes and dead requests go
   *  silently; once the kit has moved (COLLECTED / RETURNED) the confirm
   *  spells out what leaves the register. Attached signed sheets are
   *  removed from storage first (the DB rows cascade); the audit log keeps
   *  a detached note. Returns true when the row is gone. */
  const deleteBooking = async (b: EquipmentBooking): Promise<boolean> => {
    if (!isAdmin) return false;
    if (isDemoMode.value) { toast("Demo mode — nothing is deleted."); return false; }
    const attached = docsFor(b.id).filter((d) => !d.archived_at);
    const warning = deleteWarning(b.status);
    const parts = [
      `Delete ${b.reference} (${BOOKING_STATUS_LABEL[b.status]}) permanently?`,
      warning ?? "This removes the request and its items. Use Cancel instead if you want to keep a record.",
    ];
    if (attached.length > 0) {
      parts.push(`${attached.length} attached document${attached.length === 1 ? "" : "s"} will be deleted too.`);
    }
    parts.push("This cannot be undone.");
    if (!confirm(parts.join("\n\n"))) return false;
    try {
      if (attached.length > 0) await storageDelete("equipment-docs", attached.map((d) => d.path));
      await restDelete("equipment_bookings", `id=eq.${b.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Delete failed");
      await load();
      return false;
    }
    await logEvent({
      event_type: "deleted",
      from_status: b.status,
      to_status: "",
      note: `Deleted ${b.reference} (${BOOKING_STATUS_LABEL[b.status]} · ${displayName(b.requester_name)}, ${b.pickup_date} → ${b.return_date}${attached.length > 0 ? ` · ${attached.length} document${attached.length === 1 ? "" : "s"}` : ""})`,
    });
    toast(`${b.reference} deleted`);
    await load();
    return true;
  };

  // Lazy SheetJS export (same pattern as the Dashboard's Excel button) —
  // reproduces the register's familiar sheets from live data.
  const onExport = async (): Promise<void> => {
    try {
      const { exportEquipmentToExcel } = await import("./equipmentExport");
      await exportEquipmentToExcel({ items, bookings, lines });
      toast("Register downloaded");
    } catch (err) {
      console.error("equipment export failed:", err);
      toast("Could not generate the workbook — check the console.");
    }
  };

  /* ---- KPI header ---- */
  const todayIso = new Date().toISOString().slice(0, 10);
  const pendingCount = bookings.filter((b) => b.status === "PENDING").length;
  const outCount = bookings.filter((b) => b.status === "COLLECTED").length;
  const overdueCount = bookings.filter((b) => isOverdue(b, todayIso)).length;
  const todayAvail = useMemo(
    () => computeAvailability(items, bookings, lines, todayIso, todayIso),
    [items, bookings, lines, todayIso],
  );
  const zeroToday = todayAvail.filter((a) => a.free === 0).length;
  const damagedByItem = useMemo(
    () => new Map(todayAvail.map((a) => [a.item.id, { n: a.damaged, notes: a.damageNotes }])),
    [todayAvail],
  );
  const availNow = useMemo(
    () => new Map(todayAvail.map((a) => [a.item.id, { free: a.free, out: a.out, reserved: a.reserved }])),
    [todayAvail],
  );

  const failedNotifCount = notifications.filter((n) => notificationState(n) === "failed").length;
  const openTransfers = transfers.filter((t) => t.status === "PENDING" && !t.approved_at).length;
  const disputedCount = reports.filter((r) => r.status === "DISPUTED").length;

  // Requester tabs for everyone; one Manage tab for admins. The server
  // enforces the same split (RLS + RPC guards, migrations 22/23) — this
  // gating is presentation.
  // Things waiting for ME as a holder (shown on My requests).
  const mineWaiting = bookings.filter((b) =>
    (b.status === "RETURN_PENDING" && (s.profileId ? b.requester_id === s.profileId : b.requester_name === me))
    || transfers.some((t) => t.booking_id === b.id && t.status === "PENDING" && !t.accepted_at
        && (isDemoMode.value ? t.to_name === me : t.to_profile_id === s.profileId))
    || ((b.status === "PENDING" || b.status === "APPROVED") && !!me && b.pickup_delegate === me && b.requester_name !== me && b.on_behalf_of !== me),
  ).length;
  const SUBTABS: Array<{ key: SubTab; label: string; badge?: number; adminOnly?: boolean; icon?: string }> = [
    { key: "book", label: "Book" },
    { key: "mine", label: "My requests", badge: mineWaiting },
    { key: "manage", label: "Manage", badge: pendingCount + overdueCount + openTransfers + disputedCount, adminOnly: true },
    { key: "guide", label: "How it works", icon: "ⓘ" },
  ];
  const visibleSubtabs = SUBTABS.filter((t) => isAdmin || !t.adminOnly);
  const MANAGE_TABS: Array<{ key: ManageTab; label: string; badge?: number }> = [
    { key: "queue", label: "Requests", badge: pendingCount + openTransfers },
    { key: "moves", label: "Pickups & returns", badge: overdueCount + disputedCount },
    { key: "availability", label: "Availability" },
    { key: "inventory", label: "Inventory" },
    { key: "archive", label: "Archive" },
    { key: "notifications", label: "Notifications", badge: failedNotifCount },
  ];

  if (loading) {
    return <div class="eq-view"><div class="eq-empty">Loading equipment data…</div></div>;
  }
  if (loadError) {
    return (
      <div class="eq-view">
        <div class="eq-empty">
          Couldn't load the equipment module — {loadError}. Has migration
          21_equipment.sql been applied?
        </div>
      </div>
    );
  }

  return (
    <div class="eq-view">
      <nav class="eq-subtabs" role="tablist" aria-label="Equipment sections">
        {visibleSubtabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={sub === t.key}
            class={sub === t.key ? "active" : ""}
            onClick={() => setSub(t.key)}
          >
            {t.icon && <span class="eq-tab-icon" aria-hidden="true">{t.icon} </span>}
            {t.label}
            {t.badge !== undefined && t.badge > 0 && <span class="eq-badge">{t.badge}</span>}
          </button>
        ))}
      </nav>

      {sub === "guide" && <EquipmentGuide isAdmin={isAdmin} />}
      {sub === "book" && (
        <EquipmentWizard
          items={items}
          bookings={bookings}
          lines={lines}
          onCreated={() => void afterAction()}
        />
      )}
      {sub === "mine" && (
        <MyRequests
          bookings={bookings}
          transfers={transfers}
          items={items}
          linesFor={linesFor}
          me={me}
          profileId={s.profileId}
          isAdmin={isAdmin}
          closures={s.closures ?? {}}
          renderCard={(b, actions) => (
            <BookingCard key={b.id} b={b} items={items} lines={linesFor(b.id)} todayIso={todayIso} extra={renderExtra(b)} actions={actions} />
          )}
          cancelBooking={cancelBooking}
          afterAction={afterAction}
        />
      )}
      {sub === "manage" && isAdmin && (
        <div class="eq-manage">
          <div class="eq-kpis" aria-label="Equipment key numbers">
            <span class="eq-kpi"><b>{pendingCount}</b> pending</span>
            <span class="eq-kpi"><b>{outCount}</b> out now</span>
            <span class={`eq-kpi${overdueCount > 0 ? " eq-kpi-warn" : ""}`}><b>{overdueCount}</b> overdue</span>
            <span class="eq-kpi"><b>{zeroToday}</b> items at zero today</span>
            <span class="eq-kpi" title="Return-report PDFs stored in Supabase (Free plan: 1 GB). Bulk-download and free the space under Archive; history older than 24 months can be purged under Inventory.">
              <b>{formatBytes(docBytes)}</b> in {liveDocs.length} document{liveDocs.length === 1 ? "" : "s"} · of 1 GB
            </span>
            <button
              type="button"
              class="eq-action eq-export"
              onClick={() => void onExport()}
              title="Download the register as an Excel workbook (availability + per-category logs + inventory)"
            >
              ↓ Export register
            </button>
          </div>

          <nav class="eq-subtabs eq-manage-nav" role="tablist" aria-label="Manage sections">
            {MANAGE_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={manageTab === t.key}
                class={manageTab === t.key ? "active" : ""}
                onClick={() => setManageTab(t.key)}
              >
                {t.label}
                {t.badge !== undefined && t.badge > 0 && <span class="eq-badge">{t.badge}</span>}
              </button>
            ))}
          </nav>

          {manageTab === "queue" && (
            <AdminQueue
              bookings={bookings}
              items={items}
              lines={lines}
              linesFor={linesFor}
              me={me}
              todayIso={todayIso}
              closures={s.closures ?? {}}
              transition={transition}
              cancelBooking={cancelBooking}
              deleteBooking={deleteBooking}
              logEvent={logEvent}
              reload={afterAction}
              extra={(b) => renderExtra(b)}
            />
          )}
          {manageTab === "moves" && (
            <PickupsReturns
              bookings={bookings}
              items={items}
              linesFor={linesFor}
              reportFor={reportFor}
              me={me}
              todayIso={todayIso}
              transition={transition}
              reload={afterAction}
              extra={renderExtra}
            />
          )}
          {manageTab === "archive" && (
            <ArchiveTab
              bookings={bookings}
              reports={reports}
              docs={docs}
              me={me}
              onChanged={load}
              logEvent={logEvent}
            />
          )}
          {manageTab === "availability" && (
            <AvailabilityTab items={items} bookings={bookings} lines={lines} />
          )}
          {manageTab === "inventory" && (
            <InventoryTab
              categories={categories}
              items={items}
              bookings={bookings}
              availNow={availNow}
              damagedByItem={damagedByItem}
              logEvent={logEvent}
              reload={load}
              retention={{ cutoff: retentionCutoff, bookings: purgeable.length, documents: liveDocs.filter((d) => purgeable.some((b) => b.id === d.booking_id)).length, onPurge: purgeOld }}
            />
          )}
          {manageTab === "notifications" && (
            <NotificationsPanel
              notifications={notifications}
              bookings={bookings}
              busy={notifBusy}
              error={notifError}
              demo={isDemoMode.value}
              onSend={sendPending}
              onRetry={retryFailed}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── shared booking list ───────────────────── */

function BookingList({
  bookings, items, linesFor, todayIso, emptyText, actions, extra,
}: {
  bookings: EquipmentBooking[];
  items: EquipmentItem[];
  linesFor: (id: number) => EquipmentBookingLine[];
  todayIso: string;
  emptyText: string;
  actions?: (b: EquipmentBooking) => JSX.Element | null;
  /** Extra block under the items (documents, hand-over, return report). */
  extra?: (b: EquipmentBooking) => JSX.Element | null;
}): JSX.Element {
  if (bookings.length === 0) return <div class="eq-empty">{emptyText}</div>;
  return (
    <ul class="eq-booking-list">
      {bookings.map((b) => (
        <BookingCard key={b.id} b={b} items={items} lines={linesFor(b.id)} todayIso={todayIso} extra={extra?.(b) ?? null} actions={actions?.(b) ?? null} />
      ))}
    </ul>
  );
}

/** One booking, readable at a glance: reference + status, one plain
 *  sentence on what happens next, who and what, then whatever hangs
 *  under it (documents, hand-over, return report) and the verbs. */
function BookingCard({
  b, items, lines, todayIso, extra, actions,
}: {
  b: EquipmentBooking;
  items: EquipmentItem[];
  lines: EquipmentBookingLine[];
  todayIso: string;
  extra: JSX.Element | null;
  actions: JSX.Element | null;
}): JSX.Element {
  const overdue = isOverdue(b, todayIso);
  const next = nextStep(b, todayIso, {
    when: (d, t) => `${fmtDate(d)}${t ? ` at ${t}` : ""}`,
    person: displayName,
  });
  const holder = b.on_behalf_of || b.requester_name;
  const bookedBy = b.on_behalf_of && b.on_behalf_of !== b.requester_name ? b.requester_name : "";
  const condition = lines
    .filter((l) => l.qty_damaged > 0 || l.qty_lost > 0)
    .map((l) => `${items.find((i) => i.id === l.item_id)?.name ?? "item"}: ${[l.qty_damaged > 0 ? `${l.qty_damaged} damaged` : "", l.qty_lost > 0 ? `${l.qty_lost} lost` : ""].filter(Boolean).join(", ")}`)
    .join(" · ");
  return (
    <li id={`eq-booking-${b.id}`} class={`eq-booking${overdue ? " eq-overdue" : ""}`}>
      <div class="eq-booking-head">
        <b class="eq-ref">{b.reference}</b>
        <span class={`eq-chip ${STATUS_CLS[b.status] ?? "eqs-returned"}`}>{statusLabel(b.status)}</span>
        {b.status === "RETURNED" && <span class="eq-outcome">{returnOutcome(lines)}</span>}
      </div>
      <p class={`eq-next eq-next-${next.tone}`}>{next.text}</p>
      <div class="eq-booking-meta">
        <b>{displayName(holder)}</b>{bookedBy && ` (booked by ${displayName(bookedBy)})`} · {lineSummary(lines, items) || "no items"}
        {b.purpose && <span class="eq-purpose"> · {b.purpose}</span>}
        {condition && <span class="eq-condition"> · {condition}</span>}
      </div>
      {extra}
      {actions && <div class="eq-booking-actions">{actions}</div>}
    </li>
  );
}

/* ────────────────────────── admin: requests queue ─────────────────── */

type QueueFilter = BookingStatus | "ALL";
const QUEUE_FILTERS: Array<{ key: QueueFilter; label: string }> = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "COLLECTED", label: "Collected" },
  { key: "RETURN_PENDING", label: "Return to confirm" },
  { key: "RETURNED", label: "Returned" },
  { key: "REJECTED", label: "Rejected" },
  { key: "CANCELLED", label: "Cancelled" },
  { key: "ALL", label: "All" },
];

/** Admin queue: every booking, filterable by status (Pending by default),
 *  with Review / Edit / Cancel / Delete per row where the state allows. */
function AdminQueue({
  bookings, items, lines, linesFor, me, todayIso, closures, transition, cancelBooking, deleteBooking, logEvent, reload, extra,
}: {
  bookings: EquipmentBooking[];
  items: EquipmentItem[];
  lines: EquipmentBookingLine[];
  linesFor: (id: number) => EquipmentBookingLine[];
  me: string;
  todayIso: string;
  closures: Record<string, string>;
  transition: (b: EquipmentBooking, to: BookingStatus, patch: Record<string, unknown>, note?: string) => Promise<boolean>;
  cancelBooking: (b: EquipmentBooking) => Promise<void>;
  deleteBooking: (b: EquipmentBooking) => Promise<boolean>;
  logEvent: (row: Record<string, unknown>) => Promise<void>;
  reload: () => Promise<void>;
  extra?: (b: EquipmentBooking) => JSX.Element | null;
}): JSX.Element {
  const [deciding, setDeciding] = useState<EquipmentBooking | null>(null);
  const [editing, setEditing] = useState<EquipmentBooking | null>(null);
  const [filter, setFilter] = useState<QueueFilter>("PENDING");

  const shown = bookings
    .filter((b) => filter === "ALL" || b.status === filter)
    .sort((a, b) =>
      filter === "PENDING"
        ? a.pickup_date.localeCompare(b.pickup_date)
        : b.pickup_date.localeCompare(a.pickup_date),
    );
  const count = (k: QueueFilter): number =>
    k === "ALL" ? bookings.length : bookings.filter((b) => b.status === k).length;

  return (
    <>
      <div class="eq-queue-filters" role="tablist" aria-label="Filter requests by status">
        {QUEUE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            class={`eq-filter${filter === f.key ? " active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label} <span class="eq-filter-n">{count(f.key)}</span>
          </button>
        ))}
      </div>
      <BookingList
        bookings={shown}
        items={items}
        linesFor={linesFor}
        todayIso={todayIso}
        emptyText={filter === "PENDING" ? "No pending requests — all clear." : "Nothing with this status."}
        extra={extra}
        actions={(b) => (
          <>
            {b.status === "PENDING" && (
              <button type="button" class="eq-action" onClick={() => setDeciding(b)}>Review…</button>
            )}
            <button type="button" class="eq-action" onClick={() => setEditing(b)}>Edit…</button>
            {(b.status === "PENDING" || b.status === "APPROVED") && (
              <button type="button" class="eq-action eq-action-danger" onClick={() => void cancelBooking(b)}>Cancel</button>
            )}
            <button
              type="button"
              class="eq-action eq-action-danger"
              onClick={() => void deleteBooking(b)}
              title={deleteWarning(b.status) ?? "Remove this request permanently"}
            >
              Delete
            </button>
          </>
        )}
      />
      {deciding && (
        <DecideModal
          booking={deciding}
          items={items}
          lines={linesFor(deciding.id)}
          me={me}
          onClose={() => setDeciding(null)}
          transition={transition}
          reload={reload}
        />
      )}
      {editing && (
        <EditBookingModal
          booking={editing}
          items={items}
          bookings={bookings}
          lines={lines}
          bookingLines={linesFor(editing.id)}
          closures={closures}
          onClose={() => setEditing(null)}
          onDelete={deleteBooking}
          logEvent={logEvent}
          reload={reload}
        />
      )}
    </>
  );
}

function DecideModal({
  booking, items, lines, me, onClose, transition, reload,
}: {
  booking: EquipmentBooking;
  items: EquipmentItem[];
  lines: EquipmentBookingLine[];
  me: string;
  onClose: () => void;
  transition: (b: EquipmentBooking, to: BookingStatus, patch: Record<string, unknown>, note?: string) => Promise<boolean>;
  reload: () => Promise<void>;
}): JSX.Element {
  const [qtys, setQtys] = useState<Record<number, number>>(() => {
    const m: Record<number, number> = {};
    lines.forEach((l) => { m[l.id] = l.qty_requested; });
    return m;
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const itemName = (id: number): string => items.find((i) => i.id === id)?.name ?? "?";
  const changed = lines.some((l) => (qtys[l.id] ?? l.qty_requested) !== l.qty_requested);
  const allZero = lines.every((l) => (qtys[l.id] ?? 0) === 0);

  const approve = async (): Promise<void> => {
    if (allZero) { toast("Approve at least one unit — or reject the request."); return; }
    if (changed && !reason.trim()) { toast("A reason is required when reducing quantities."); return; }
    setBusy(true);
    try {
      for (const l of lines) {
        const q = Math.max(0, Math.min(qtys[l.id] ?? l.qty_requested, l.qty_requested));
        await restPatch("equipment_booking_lines", `id=eq.${l.id}`, { qty_approved: q });
      }
      const ok = await transition(booking, "APPROVED", {
        decided_by: displayName(me),
        decided_at: nowIso(),
        decision_reason: reason.trim(),
      }, changed ? `Approved with changes: ${reason.trim()}` : "Approved as requested");
      if (ok) {
        toast(`${booking.reference} approved`);
        onClose();
        await reload();
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async (): Promise<void> => {
    if (!reason.trim()) { toast("A reason is required to reject."); return; }
    setBusy(true);
    const ok = await transition(booking, "REJECTED", {
      decided_by: displayName(me),
      decided_at: nowIso(),
      decision_reason: reason.trim(),
    }, reason.trim());
    setBusy(false);
    if (ok) {
      toast(`${booking.reference} rejected`);
      onClose();
      await reload();
    }
  };

  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="eqDecideTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqDecideTitle">Review {booking.reference}</h3>
        <div class="modal-date">
          {displayName(booking.requester_name)}
          {booking.on_behalf_of && ` (for ${booking.on_behalf_of})`} · {booking.pickup_date} {booking.pickup_time} → {booking.return_date}
        </div>
        {booking.purpose && <div class="eq-modal-purpose">{booking.purpose}</div>}
        {booking.requester_notes && <div class="eq-modal-purpose">Notes: {booking.requester_notes}</div>}
        <div class="eq-decide-lines">
          {lines.map((l) => (
            <label key={l.id} class="eq-decide-line">
              <span class="eq-decide-name">{itemName(l.item_id)}</span>
              <span class="eq-decide-qty">
                <input
                  type="number"
                  min={0}
                  max={l.qty_requested}
                  value={qtys[l.id] ?? l.qty_requested}
                  onInput={(e) => {
                    const v = Number((e.target as HTMLInputElement).value);
                    setQtys((m) => ({ ...m, [l.id]: Number.isFinite(v) ? v : 0 }));
                  }}
                />
                <span class="eq-decide-of">of {l.qty_requested} requested</span>
              </span>
            </label>
          ))}
        </div>
        <label class="eq-field eq-field-wide">
          <span>Reason {changed ? "(required — quantities changed)" : "(required for rejection)"}</span>
          <textarea
            value={reason}
            onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            maxLength={300}
            placeholder="Shown to the requester"
          />
        </label>
        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-secondary eq-reject-btn" disabled={busy} onClick={() => void reject()}>
            Reject
          </button>
          <button type="button" class="modal-primary" disabled={busy} onClick={() => void approve()}>
            {busy ? "Saving…" : changed ? "Approve with changes" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── admin: pickups & returns ─────────────────────── */

function PickupsReturns({
  bookings, items, linesFor, reportFor, me, todayIso, transition, reload, extra,
}: {
  bookings: EquipmentBooking[];
  items: EquipmentItem[];
  linesFor: (id: number) => EquipmentBookingLine[];
  reportFor: (id: number) => EquipmentReturnReport | null;
  me: string;
  todayIso: string;
  transition: (b: EquipmentBooking, to: BookingStatus, patch: Record<string, unknown>, note?: string) => Promise<boolean>;
  reload: () => Promise<void>;
  extra?: (b: EquipmentBooking, onAmend?: (b: EquipmentBooking) => void) => JSX.Element | null;
}): JSX.Element {
  const [pickingUp, setPickingUp] = useState<EquipmentBooking | null>(null);
  const [returning, setReturning] = useState<EquipmentBooking | null>(null);

  const toPickUp = bookings
    .filter((b) => b.status === "APPROVED")
    .sort((a, b) => a.pickup_date.localeCompare(b.pickup_date));
  const out = bookings
    .filter((b) => b.status === "COLLECTED")
    .sort((a, b) => a.return_date.localeCompare(b.return_date));
  const toConfirm = bookings
    .filter((b) => b.status === "RETURN_PENDING")
    .sort((a, b) => (reportFor(a.id)?.declared_at ?? "").localeCompare(reportFor(b.id)?.declared_at ?? ""));

  return (
    <div class="eq-moves">
      <h3 class="eq-section-title">Awaiting pickup ({toPickUp.length})</h3>
      <BookingList
        bookings={toPickUp}
        items={items}
        linesFor={linesFor}
        todayIso={todayIso}
        emptyText="Nothing awaiting pickup."
        extra={extra}
        actions={(b) => (
          <button type="button" class="eq-action" onClick={() => setPickingUp(b)}>
            Confirm pickup…
          </button>
        )}
      />
      <h3 class="eq-section-title">Currently out ({out.length})</h3>
      <BookingList
        bookings={out}
        items={items}
        linesFor={linesFor}
        todayIso={todayIso}
        emptyText="Nothing out right now."
        extra={extra ? (b) => extra(b) : undefined}
        actions={(b) => (
          <button type="button" class="eq-action" onClick={() => setReturning(b)} title="Check the items and issue the return report the holder must confirm">
            Issue return report…
          </button>
        )}
      />
      <h3 class="eq-section-title">Returns awaiting the holder's confirmation ({toConfirm.length})</h3>
      <p class="eq-inventory-hint">
        The kit is back and the report has been sent. The booking closes when the holder confirms it — or when you
        close it without confirmation (note required). A disputed report can be amended and re-issued.
      </p>
      <BookingList
        bookings={toConfirm}
        items={items}
        linesFor={linesFor}
        todayIso={todayIso}
        emptyText="No return waiting to be confirmed."
        extra={extra ? (b) => extra(b, setReturning) : undefined}
      />
      {pickingUp && (
        <PickupModal
          booking={pickingUp}
          me={me}
          onClose={() => setPickingUp(null)}
          transition={transition}
          reload={reload}
        />
      )}
      {returning && (
        <ReturnModal
          booking={returning}
          items={items}
          lines={linesFor(returning.id)}
          report={reportFor(returning.id)}
          me={me}
          onClose={() => setReturning(null)}
          onIssued={reload}
        />
      )}
    </div>
  );
}

function PickupModal({
  booking, me, onClose, transition, reload,
}: {
  booking: EquipmentBooking;
  me: string;
  onClose: () => void;
  transition: (b: EquipmentBooking, to: BookingStatus, patch: Record<string, unknown>, note?: string) => Promise<boolean>;
  reload: () => Promise<void>;
}): JSX.Element {
  // Pre-filled with the colleague the holder delegated, if any.
  const [collectedBy, setCollectedBy] = useState(booking.pickup_delegate || booking.on_behalf_of || booking.requester_name);
  const [supervisor, setSupervisor] = useState(me);
  const [busy, setBusy] = useState(false);

  const confirmPickup = async (): Promise<void> => {
    if (!supervisor) { toast("Choose the General Affairs colleague handing over."); return; }
    if (!collectedBy) { toast("Choose who is collecting."); return; }
    setBusy(true);
    const ok = await transition(booking, "COLLECTED", {
      collected_at: nowIso(),
      collected_by: collectedBy,
      pickup_supervisor: supervisor,
    }, `Collected by ${collectedBy}, handed over by ${supervisor}`);
    setBusy(false);
    if (ok) {
      toast(`${booking.reference} marked as collected`);
      onClose();
      await reload();
    }
  };

  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="eqPickupTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqPickupTitle">Confirm pickup — {booking.reference}</h3>
        <div class="modal-date">{booking.pickup_date} {booking.pickup_time}</div>
        <div class="eq-form-grid">
          <PersonSelect label="Collected by" value={collectedBy} onChange={setCollectedBy} wide />
          <PersonSelect label="General Affairs — handed over by" value={supervisor} onChange={setSupervisor} wide />
        </div>
        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-primary" disabled={busy} onClick={() => void confirmPickup()}>
            {busy ? "Saving…" : "Confirm pickup"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── admin: availability ──────────────────────── */

function AvailabilityTab({
  items, bookings, lines,
}: {
  items: EquipmentItem[];
  bookings: EquipmentBooking[];
  lines: EquipmentBookingLine[];
}): JSX.Element {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(todayIso);
  const [to, setTo] = useState(todayIso);
  const valid = !!from && !!to && from <= to;
  const availability = useMemo(
    () => (valid ? computeAvailability(items, bookings, lines, from, to) : []),
    [valid, items, bookings, lines, from, to],
  );
  return (
    <div class="eq-card">
      <div class="eq-form-grid">
        <label class="eq-field">
          <span>From</span>
          <input type="date" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} />
        </label>
        <label class="eq-field">
          <span>To</span>
          <input type="date" value={to} min={from} onInput={(e) => setTo((e.target as HTMLInputElement).value)} />
        </label>
      </div>
      {!valid && <div class="eq-empty">Pick a valid window.</div>}
      {valid && (
        <div class="eq-scroll">
          <table class="eq-table" aria-label="Availability by item">
            <thead>
              <tr>
                <th>Item</th><th class="num">Total</th><th class="num">Lost</th>
                <th class="num" title="Units set aside as damaged — not bookable until an admin marks them repaired in Inventory">Damaged</th>
                <th class="num">Out</th>
                <th class="num">Reserved</th><th class="num">Free</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {availability.slice().sort((x, y) => x.item.name.localeCompare(y.item.name)).map((a) => {
                const meta = AVAILABILITY_STATUS_META[a.status];
                return (
                  <tr key={a.item.id}>
                    <td><b>{a.item.name}</b></td>
                    <td class="num">{a.item.total_qty}</td>
                    <td class="num">{a.item.lost_qty || ""}</td>
                    <td class={`num${a.damaged ? " eq-damaged" : ""}`} title={a.damageNotes.join("\n")}>{a.damaged || ""}</td>
                    <td class="num">{a.out || ""}</td>
                    <td class="num">{a.reserved || ""}</td>
                    <td class="num"><b>{a.free}</b></td>
                    <td><span class={`eq-chip ${meta.cls}`}><span aria-hidden="true">{meta.icon}</span> {meta.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── admin: inventory ─────────────────────────── */

/** Inventory. The roster (Total) is fixed; every unit is Available,
 *  Damaged (set aside until repaired) or Lost. The two condition counters
 *  are changed in place with − / + — one unit per click, effective on
 *  availability at once, each click audited — so putting a repaired
 *  microphone back in circulation is one click, not a form. Edit… is for
 *  the roster itself (name, total, notes, active) and the history. */
function InventoryTab({
  categories, items, bookings, availNow, damagedByItem, logEvent, reload, retention,
}: {
  categories: EquipmentCategory[];
  items: EquipmentItem[];
  bookings: EquipmentBooking[];
  availNow: Map<number, { free: number; out: number; reserved: number }>;
  /** Damage notes from the return history (tooltip on the Damaged cell). */
  damagedByItem: Map<number, { n: number; notes: string[] }>;
  retention: { cutoff: string; bookings: number; documents: number; onPurge: () => Promise<void> };
  logEvent: (row: Record<string, unknown>) => Promise<void>;
  reload: () => Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState<EquipmentItem | null>(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  /** One unit at a time: + sets a unit aside (damaged) or writes it off
   *  (lost), − brings it back (repaired / found). */
  const adjust = async (i: EquipmentItem, kind: "damaged" | "lost", delta: 1 | -1): Promise<void> => {
    if (isDemoMode.value) { toast("Demo mode — changes aren't saved."); return; }
    const inStock = i.total_qty - i.lost_qty - i.out_of_service_qty;
    const current = kind === "damaged" ? i.out_of_service_qty : i.lost_qty;
    const next = current + delta;
    if (next < 0) return;
    let note = "";
    if (delta > 0) {
      if (inStock <= 0) { toast(`${i.name}: every unit is already damaged or lost`); return; }
      const asked = prompt(
        kind === "damaged"
          ? `Set one ${i.name} aside as damaged.\n\nWhat happened? (optional, kept in the history)`
          : `Write one ${i.name} off as lost.\n\nNote (optional, kept in the history):`,
      );
      if (asked === null) return;
      note = asked.trim();
    }
    const event = kind === "damaged" ? (delta > 0 ? "damaged" : "repaired") : (delta > 0 ? "lost" : "found");
    setBusyId(i.id);
    try {
      await restPatch("equipment_items", `id=eq.${i.id}`, {
        [kind === "damaged" ? "out_of_service_qty" : "lost_qty"]: next,
        updated_at: nowIso(),
      });
      await logEvent({ item_id: i.id, event_type: event, qty: 1, note: note || `${kind} ${current} → ${next}` });
      const bookable = i.total_qty
        - (kind === "lost" ? next : i.lost_qty)
        - (kind === "damaged" ? next : i.out_of_service_qty);
      toast(`${i.name}: ${QUICK_TOAST[event]} — ${Math.max(0, bookable)} of ${i.total_qty} bookable`);
      await reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const sorted = items.slice().sort((x, y) => Number(y.is_active) - Number(x.is_active) || x.name.localeCompare(y.name));

  return (
    <div class="eq-card">
      <div class="eq-inventory-head">
        <span class="eq-inventory-hint">
          The <b>roster</b> (Total) stays as it is. Use <b>−</b> / <b>+</b> to set a unit aside as
          <b> Damaged</b> or write it off as <b>Lost</b>; the moment you click <b>−</b> on a repaired or
          found unit it is bookable again. Confirmed returns update the same counters. Every click is
          kept in the item's history (Edit…).
        </span>
        <button type="button" class="eq-action" onClick={() => setAdding(true)}>＋ Add item</button>
      </div>
      <div class="eq-scroll">
        <table class="eq-table eq-inventory" aria-label="Inventory master">
          <thead>
            <tr>
              <th>Item</th>
              <th class="num" title="The roster — everything the unit owns">Roster</th>
              <th class="num" title="Bookable today: roster − damaged − lost − currently out">Available now</th>
              <th class="num" title="Set aside, not bookable, until marked repaired">Damaged</th>
              <th class="num" title="Gone for good">Lost</th>
              <th>Notes</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((i) => {
              const a = availNow.get(i.id);
              const inStock = i.total_qty - i.lost_qty - i.out_of_service_qty;
              const free = a?.free ?? Math.max(0, inStock);
              const busy = busyId === i.id;
              const dead = !i.is_active;
              return (
                <tr key={i.id} class={dead ? "eq-inactive" : ""}>
                  <td><b>{i.name}</b>{dead && <span class="former-tag">Inactive</span>}</td>
                  <td class="num">{i.total_qty}</td>
                  <td class={`num eq-avail-now${free === 0 ? " zero" : ""}`}>
                    <b>{free}</b>
                    {a && a.out > 0 && <span class="eq-avail-sub">{a.out} out</span>}
                    {a && a.reserved > 0 && <span class="eq-avail-sub">{a.reserved} reserved</span>}
                  </td>
                  <td class={`num${i.out_of_service_qty ? " eq-damaged" : ""}`} title={(damagedByItem.get(i.id)?.notes ?? []).join("\n")}>
                    <span class="eq-stepper eq-qstep" role="group" aria-label={`Damaged units of ${i.name}`}>
                      <button type="button" disabled={busy || dead || i.out_of_service_qty === 0} onClick={() => void adjust(i, "damaged", -1)} title="One unit repaired — back in circulation" aria-label={`Mark one ${i.name} repaired`}>−</button>
                      <span>{i.out_of_service_qty}</span>
                      <button type="button" disabled={busy || dead || inStock <= 0} onClick={() => void adjust(i, "damaged", 1)} title="Set one unit aside as damaged" aria-label={`Mark one ${i.name} damaged`}>+</button>
                    </span>
                  </td>
                  <td class={`num${i.lost_qty ? " eq-lost" : ""}`}>
                    <span class="eq-stepper eq-qstep" role="group" aria-label={`Lost units of ${i.name}`}>
                      <button type="button" disabled={busy || dead || i.lost_qty === 0} onClick={() => void adjust(i, "lost", -1)} title="One unit found again" aria-label={`Mark one ${i.name} found`}>−</button>
                      <span>{i.lost_qty}</span>
                      <button type="button" disabled={busy || dead || inStock <= 0} onClick={() => void adjust(i, "lost", 1)} title="Write one unit off as lost" aria-label={`Mark one ${i.name} lost`}>+</button>
                    </span>
                  </td>
                  <td class="eq-notes-cell">{i.unit_notes}</td>
                  <td><button type="button" class="eq-action eq-action-quiet" onClick={() => setEditing(i)}>Edit…</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div class="eq-retention">
        <div class="eq-section-title">Storage &amp; retention</div>
        <p class="eq-inventory-hint">
          Booking history and return reports are kept for <b>24 months</b>. Bookings returned,
          rejected or cancelled before <b>{retention.cutoff}</b> can be purged together with their
          stored documents — item stock and lost counts are unaffected.
        </p>
        <button
          type="button"
          class="eq-action eq-action-danger"
          disabled={retention.bookings === 0}
          onClick={() => void retention.onPurge()}
        >
          {retention.bookings === 0
            ? "Nothing older than 24 months"
            : `Purge ${retention.bookings} old booking${retention.bookings === 1 ? "" : "s"} · ${retention.documents} document${retention.documents === 1 ? "" : "s"}`}
        </button>
      </div>
      {(editing || adding) && (
        <ItemModal
          item={editing}
          categories={categories}
          bookings={bookings}
          onClose={() => { setEditing(null); setAdding(false); }}
          logEvent={logEvent}
          reload={reload}
        />
      )}
    </div>
  );
}

const QUICK_TOAST: Record<string, string> = {
  damaged: "1 unit set aside as damaged",
  repaired: "1 unit repaired, back in circulation",
  lost: "1 unit written off as lost",
  found: "1 unit found again",
};

interface ItemEvent {
  id: number;
  event_type: string;
  qty: number | null;
  note: string;
  actor: string;
  created_at: string;
  booking_id: number | null;
}

const ITEM_EVENT_LABEL: Record<string, string> = {
  lost: "Lost",
  found: "Found again",
  damaged: "Damaged",
  repaired: "Repaired",
  out_of_service: "Damaged (set aside)",
  qty_adjusted: "Roster adjusted",
  item_edited: "Edited",
};

/** The roster editor: name, total units, notes, active — plus the item's
 *  recent history. Condition (damaged / lost) is handled in the table. */
function ItemModal({
  item, categories, bookings, onClose, logEvent, reload,
}: {
  item: EquipmentItem | null;
  categories: EquipmentCategory[];
  bookings: EquipmentBooking[];
  onClose: () => void;
  logEvent: (row: Record<string, unknown>) => Promise<void>;
  reload: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(item?.name ?? "");
  const [total, setTotal] = useState(item?.total_qty ?? 1);
  const [notes, setNotes] = useState(item?.unit_notes ?? "");
  const [active, setActive] = useState(item?.is_active ?? true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<ItemEvent[] | null>(null);
  // The TRASFERTE / PODCAST split is no longer shown anywhere; the column
  // stays in the DB (NOT NULL FK), so new items silently take the first
  // category and existing ones keep theirs.
  const categoryId = item?.category_id ?? categories[0]?.id ?? 1;

  useEffect(() => {
    if (!item || isDemoMode.value) { setHistory([]); return; }
    restGet<ItemEvent>(
      "equipment_events",
      `item_id=eq.${item.id}&select=id,event_type,qty,note,actor,created_at,booking_id&order=id.desc&limit=10`,
    ).then(setHistory).catch(() => setHistory([]));
  }, [item]);

  const minTotal = item ? item.lost_qty + item.out_of_service_qty : 0;
  const problems: string[] = [];
  if (!name.trim()) problems.push("A name is required");
  if (!Number.isInteger(total) || total < 0) problems.push("The roster must be a whole number ≥ 0");
  else if (total < minTotal) problems.push(`The roster can't be below the ${minTotal} units currently damaged or lost`);
  const totalChanged = !!item && total !== item.total_qty;

  const save = async (): Promise<void> => {
    if (problems.length > 0) { toast(problems[0] ?? "Check the form"); return; }
    if (isDemoMode.value) { toast("Demo mode — changes aren't saved."); onClose(); return; }
    setBusy(true);
    try {
      if (item) {
        await restPatch("equipment_items", `id=eq.${item.id}`, {
          name: name.trim(),
          category_id: categoryId,
          total_qty: total,
          unit_notes: notes.trim(),
          is_active: active,
          updated_at: nowIso(),
        });
        if (totalChanged) {
          await logEvent({ item_id: item.id, event_type: "qty_adjusted", qty: total - item.total_qty, note: `roster ${item.total_qty} → ${total}${reason.trim() ? ` — ${reason.trim()}` : ""}` });
        }
        toast(`${name.trim()} saved`);
      } else {
        await restUpsert("equipment_items", [{
          name: name.trim(),
          category_id: categoryId,
          total_qty: total,
          lost_qty: 0,
          out_of_service_qty: 0,
          unit_notes: notes.trim(),
          is_active: active,
        }]);
        toast(`${name.trim()} added`);
      }
      onClose();
      await reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const refOf = (id: number | null): string => (id ? bookings.find((b) => b.id === id)?.reference ?? `#${id}` : "");

  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="eqItemTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqItemTitle">{item ? `Edit — ${item.name}` : "Add item"}</h3>
        {item && (
          <div class="modal-date">
            {item.total_qty} in the roster · {item.out_of_service_qty} damaged · {item.lost_qty} lost — change those with − / + in the table
          </div>
        )}
        <div class="eq-form-grid">
          <label class="eq-field eq-field-wide">
            <span>Name</span>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </label>
          <label class="eq-field">
            <span>Roster (total units)</span>
            <input type="number" min={minTotal} step={1} value={total}
              onInput={(e) => setTotal(Math.max(0, Math.floor(Number((e.target as HTMLInputElement).value) || 0)))} />
            <em class="eq-field-hint">Everything the unit owns. Change it only when kit is bought or dismissed.</em>
          </label>
          {totalChanged && (
            <label class="eq-field">
              <span>Why does the roster change? <i>(kept in the history)</i></span>
              <input type="text" value={reason} maxLength={200} placeholder="e.g. 2 new tripods bought, 1 phone dismissed"
                onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
            </label>
          )}
          <label class="eq-field eq-field-wide">
            <span>Unit notes</span>
            <input type="text" value={notes} maxLength={200} onInput={(e) => setNotes((e.target as HTMLInputElement).value)} />
          </label>
          <label class="checkbox-row eq-field-wide">
            <input type="checkbox" checked={active} onChange={(e) => setActive((e.target as HTMLInputElement).checked)} />
            Active (bookable)
          </label>
        </div>
        {item && (
          <div class="eq-item-history">
            <div class="eq-section-title">Recent history</div>
            {history === null && <div class="eq-inventory-hint">Loading…</div>}
            {history && history.length === 0 && <div class="eq-inventory-hint">No changes recorded yet.</div>}
            {history && history.length > 0 && (
              <ul>
                {history.map((h) => (
                  <li key={h.id}>
                    <span class="eq-hist-when">{h.created_at.slice(0, 10)}</span>
                    <b>{ITEM_EVENT_LABEL[h.event_type] ?? h.event_type}</b>
                    {h.qty !== null && h.qty !== 0 && <span class="eq-hist-qty">{h.event_type === "qty_adjusted" && h.qty > 0 ? "+" : ""}{h.qty}</span>}
                    {h.booking_id && <span class="eq-hist-ref">{refOf(h.booking_id)}</span>}
                    {h.note && <span class="eq-hist-note">{h.note}</span>}
                    {h.actor && <span class="eq-hist-actor">· {h.actor}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {problems.length > 0 && <div class="eq-problems">{problems[0]}</div>}
        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-primary" disabled={busy || problems.length > 0} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── admin: notifications ─────────────────────── */

const NOTIF_STATE_META: Record<NotificationState, { label: string; cls: string }> = {
  sent: { label: "Sent", cls: "eqs-approved" },
  skipped: { label: "Skipped", cls: "eqs-returned" },
  retrying: { label: "Retrying", cls: "eqs-pending" },
  failed: { label: "Failed", cls: "eqs-rejected" },
  pending: { label: "Pending", cls: "eqs-collected" },
};

/** The email outbox (migration 23) — one row per booking event, with its
 *  delivery state. "Send pending now" invokes the equipment-notify
 *  function; "Retry failed" resets exhausted rows and sends again. */
function NotificationsPanel({
  notifications, bookings, busy, error, demo, onSend, onRetry,
}: {
  notifications: EquipmentNotification[];
  bookings: EquipmentBooking[];
  busy: boolean;
  error: string | null;
  demo: boolean;
  onSend: () => Promise<void>;
  onRetry: () => Promise<void>;
}): JSX.Element {
  const refOf = (id: number): string => bookings.find((b) => b.id === id)?.reference ?? `#${id}`;
  const byOf = (id: number): string => {
    const b = bookings.find((x) => x.id === id);
    return b ? displayName(b.requester_name) : "—";
  };
  const failed = notifications.filter((n) => notificationState(n) === "failed").length;
  return (
    <div class="eq-card">
      <div class="eq-inventory-head">
        <span class="eq-inventory-hint">
          Every booking event queues an email here and the{" "}
          <code>equipment-notify</code> function delivers it via Resend.
          New requests go to the admins ticked 🔔 in the Directory;
          decisions, pickups and returns go to the requester.
        </span>
        <div class="eq-notif-actions">
          <button type="button" class="eq-action" disabled={busy} onClick={() => void onSend()}>
            {busy ? "Sending…" : "Send pending now"}
          </button>
          {failed > 0 && (
            <button type="button" class="eq-action eq-action-danger" disabled={busy} onClick={() => void onRetry()}>
              Retry {failed} failed
            </button>
          )}
        </div>
      </div>
      {error && <div class="eq-problems">{error}</div>}
      {demo && <div class="eq-notif-demo">Demo mode — these rows are synthetic; nothing is sent.</div>}
      {notifications.length === 0 && !error ? (
        <div class="eq-empty">No notifications yet — they appear as soon as a booking is created or changes status.</div>
      ) : (
        <div class="eq-scroll">
          <table class="eq-table" aria-label="Notification outbox">
            <thead>
              <tr>
                <th>When</th><th>Booking</th><th>Booked by</th><th>Event</th><th>Sent to</th><th>State</th><th>Details</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((n) => {
                const st = notificationState(n);
                const meta = NOTIF_STATE_META[st];
                return (
                  <tr key={n.id}>
                    <td class="eq-notif-when">{n.created_at.slice(0, 16).replace("T", " ")}</td>
                    <td><b>{refOf(n.booking_id)}</b></td>
                    <td class="eq-notif-by">{byOf(n.booking_id)}</td>
                    <td>{n.kind.replace("_", " ")}</td>
                    <td class="eq-notif-to">
                      {n.recipients.length > 0 ? n.recipients.join(", ") : NOTIFICATION_AUDIENCE[n.kind]}
                    </td>
                    <td>
                      <span class={`eq-chip ${meta.cls}`}>
                        {meta.label}{n.attempts > 1 && st !== "sent" ? ` · ${n.attempts} tries` : ""}
                      </span>
                    </td>
                    <td class="eq-notif-err">{n.last_error ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────── admin: edit a booking ────────────────────── */

/** Admin edit of any booking: who it's for, the window (same
 *  weekday/holiday/closure rules as the wizard, but a date already in the
 *  past is allowed so live bookings can be fixed), purpose/notes, and item
 *  quantities. While the request is open (PENDING / APPROVED) quantities
 *  are checked against availability for the NEW window with this
 *  booking's own reservation excluded. Once COLLECTED the pickup and
 *  quantities are physical facts and stay read-only. Closed records
 *  (RETURNED / REJECTED / CANCELLED) can be corrected freely — they hold
 *  no reservation, so stock and availability are unaffected — except for
 *  quantities, which the return registration already settled. The modal
 *  also carries the permanent Delete, so an admin can open a record,
 *  check it, and remove it in one place. */
function EditBookingModal({
  booking, items, bookings, lines, bookingLines, closures, onClose, onDelete, logEvent, reload,
}: {
  booking: EquipmentBooking;
  items: EquipmentItem[];
  bookings: EquipmentBooking[];
  lines: EquipmentBookingLine[];
  bookingLines: EquipmentBookingLine[];
  closures: Record<string, string>;
  onClose: () => void;
  onDelete: (b: EquipmentBooking) => Promise<boolean>;
  logEvent: (row: Record<string, unknown>) => Promise<void>;
  reload: () => Promise<void>;
}): JSX.Element {
  const s = state.value;
  const collected = booking.status === "COLLECTED" || booking.status === "RETURN_PENDING";
  const closed = booking.status === "RETURNED" || booking.status === "REJECTED" || booking.status === "CANCELLED";
  // Quantities only matter while the request is open; afterwards they
  // are what was handed over / registered back.
  const qtyLocked = collected || closed;
  const people = useMemo(() => {
    const list = activePeople(s.roster).sort(bySurname);
    for (const n of [booking.requester_name, booking.on_behalf_of]) {
      if (n && !list.includes(n)) list.unshift(n);
    }
    return list;
  }, [s.roster, booking.requester_name, booking.on_behalf_of]);

  const [forWhom, setForWhom] = useState(booking.on_behalf_of || booking.requester_name);
  const [delegate, setDelegate] = useState(booking.pickup_delegate ?? "");
  const [pickupDate, setPickupDate] = useState(booking.pickup_date);
  const [pickupTime, setPickupTime] = useState(booking.pickup_time);
  const [returnDate, setReturnDate] = useState(booking.return_date);
  const [returnTime, setReturnTime] = useState(booking.return_time);
  const [purpose, setPurpose] = useState(booking.purpose);
  const [notes, setNotes] = useState(booking.requester_notes);
  const [adminNotes, setAdminNotes] = useState(booking.admin_notes);
  const [qtys, setQtys] = useState<Record<number, number>>(() => {
    const m: Record<number, number> = {};
    bookingLines.forEach((l) => { m[l.id] = l.qty_approved ?? l.qty_requested; });
    return m;
  });
  const [busy, setBusy] = useState(false);

  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      if (await onDelete(booking)) onClose();
    } finally {
      setBusy(false);
    }
  };

  const deps = {
    isWeekend,
    getHolidayName: (iso: string) => getHoliday(iso)?.name ?? null,
    closures,
  };
  // "" as today disables the past-date check for admin edits. Closed
  // records are corrections of what happened, so the weekend / closure
  // rules (which protect future planning) don't apply — only the order
  // of the two dates does.
  const pickupProblem = closed ? null : dateProblem(pickupDate, "", deps);
  const returnProblem =
    (closed ? null : dateProblem(returnDate, "", deps))
    ?? (returnDate < pickupDate ? "Return can't precede pickup" : null);

  const free = useMemo(() => {
    const m = new Map<number, number>();
    if (qtyLocked || pickupProblem || returnProblem) return m;
    const others = bookings.filter((b) => b.id !== booking.id);
    const otherLines = lines.filter((l) => l.booking_id !== booking.id);
    computeAvailability(items, others, otherLines, pickupDate, returnDate).forEach((a) => m.set(a.item.id, a.free));
    return m;
  }, [items, bookings, lines, booking.id, pickupDate, returnDate, pickupProblem, returnProblem, qtyLocked]);

  const itemName = (id: number): string => items.find((i) => i.id === id)?.name ?? "?";
  const qtyProblems = qtyLocked ? [] : bookingLines.flatMap((l) => {
    const q = qtys[l.id] ?? 0;
    if (q < 1) return [`${itemName(l.item_id)}: quantity must be at least 1`];
    const f = free.get(l.item_id);
    if (f !== undefined && q > f) return [`${itemName(l.item_id)}: only ${f} free for this window`];
    return [];
  });
  const problems = [pickupProblem, returnProblem, ...qtyProblems].filter((p): p is string => !!p);

  const save = async (): Promise<void> => {
    if (problems.length > 0) { toast(problems[0] ?? "Check the form"); return; }
    setBusy(true);
    try {
      const onBehalf = forWhom && forWhom !== booking.requester_name ? forWhom : "";
      await restPatch("equipment_bookings", `id=eq.${booking.id}`, {
        on_behalf_of: onBehalf,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        return_date: returnDate,
        return_time: returnTime,
        purpose: purpose.trim(),
        requester_notes: notes.trim(),
        admin_notes: adminNotes.trim(),
        updated_at: nowIso(),
      });
      if (!qtyLocked) {
        for (const l of bookingLines) {
          const q = qtys[l.id];
          if (q === undefined || q === (l.qty_approved ?? l.qty_requested)) continue;
          // Before a decision the request itself changes; after approval
          // the approved quantity is what reserves stock.
          await restPatch(
            "equipment_booking_lines",
            `id=eq.${l.id}`,
            booking.status === "PENDING" ? { qty_requested: q } : { qty_approved: q },
          );
        }
      }
      if (!collected && !closed && delegate !== (booking.pickup_delegate ?? "")) {
        // Through the RPC so the delegate, the holder and the admins are emailed.
        await restRpc("equipment_set_pickup_delegate", { p_booking_id: booking.id, p_name: delegate });
      }
      await logEvent({
        booking_id: booking.id,
        event_type: "edited",
        from_status: booking.status,
        to_status: booking.status,
        note: `Edited: ${pickupDate} ${pickupTime} → ${returnDate}${returnTime ? ` ${returnTime}` : ""}${onBehalf ? ` · for ${onBehalf}` : ""}`,
      });
      toast(`${booking.reference} updated`);
      onClose();
      await reload();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="modal-backdrop open" role="presentation" {...backdropHandlers(onClose)}>
      <div class="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="eqEditTitle" tabindex={-1} onClick={(e) => e.stopPropagation()}>
        <h3 id="eqEditTitle">Edit {booking.reference}</h3>
        <div class="modal-date">
          {BOOKING_STATUS_LABEL[booking.status]} · booked by {displayName(booking.requester_name)}
        </div>
        {closed && (
          <p class="eq-modal-purpose">
            Closed record — changes here correct the register only; stock and availability are unaffected and no email is sent.
          </p>
        )}
        <div class="eq-form-grid">
          <label class="eq-field">
            <span>For whom</span>
            <select value={forWhom} onChange={(e) => setForWhom((e.target as HTMLSelectElement).value)}>
              {people.map((p) => <option key={p} value={p}>{displayName(p)}</option>)}
            </select>
          </label>
          {!collected && !closed && (
            <PersonSelect label="Who will collect" value={delegate} onChange={setDelegate} placeholder="— the holder —" />
          )}
          <label class="eq-field">
            <span>Pickup date</span>
            <input type="date" value={pickupDate} disabled={collected}
              onInput={(e) => setPickupDate((e.target as HTMLInputElement).value)} />
            {pickupProblem && <em class="eq-field-err">{pickupProblem}</em>}
          </label>
          <label class="eq-field">
            <span>Pickup time</span>
            <select value={pickupTime} disabled={collected} onChange={(e) => setPickupTime((e.target as HTMLSelectElement).value)}>
              {timeSlots().map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label class="eq-field">
            <span>Expected return date</span>
            <input type="date" value={returnDate} min={pickupDate}
              onInput={(e) => setReturnDate((e.target as HTMLInputElement).value)} />
            {returnProblem && <em class="eq-field-err">{returnProblem}</em>}
          </label>
          <label class="eq-field">
            <span>Return time <i>(optional)</i></span>
            <select value={returnTime} onChange={(e) => setReturnTime((e.target as HTMLSelectElement).value)}>
              <option value="">—</option>
              {timeSlots().map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label class="eq-field eq-field-wide">
            <span>Purpose / project</span>
            <input type="text" value={purpose} maxLength={120}
              onInput={(e) => setPurpose((e.target as HTMLInputElement).value)} />
          </label>
        </div>

        <div class="eq-edit-lines">
          <div class="eq-section-title">
            Items{collected ? " (fixed once collected)" : closed ? " (as registered)" : ""}
          </div>
          {bookingLines.map((l) => {
            const f = free.get(l.item_id);
            return (
              <label key={l.id} class="eq-decide-line">
                <span class="eq-decide-name">{itemName(l.item_id)}</span>
                <span class="eq-decide-qty">
                  <input
                    type="number"
                    min={1}
                    disabled={qtyLocked}
                    value={qtys[l.id] ?? 1}
                    onInput={(e) => {
                      const v = Number((e.target as HTMLInputElement).value);
                      setQtys((m) => ({ ...m, [l.id]: Number.isFinite(v) ? v : 1 }));
                    }}
                  />
                  {!qtyLocked && f !== undefined && <span class="eq-decide-of">{f} free for this window</span>}
                </span>
              </label>
            );
          })}
        </div>

        <div class="eq-form-grid">
          <label class="eq-field">
            <span>Requester notes</span>
            <textarea value={notes} maxLength={400} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
          </label>
          <label class="eq-field">
            <span>Admin notes <i>(internal)</i></span>
            <textarea value={adminNotes} maxLength={400} onInput={(e) => setAdminNotes((e.target as HTMLTextAreaElement).value)} />
          </label>
        </div>
        {problems.length > 0 && <div class="eq-problems">{problems[0]}</div>}
        <div class="modal-actions">
          <button
            type="button"
            class="modal-secondary clear-btn eq-modal-delete"
            disabled={busy}
            onClick={() => void remove()}
            title={deleteWarning(booking.status) ?? "Remove this request permanently"}
          >
            Delete request…
          </button>
          <button type="button" class="modal-secondary" onClick={onClose}>Close</button>
          <button type="button" class="modal-primary" disabled={busy || problems.length > 0} onClick={() => void save()}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── documents under a booking ───────────────────── */

/** Stored return-report PDF (or a legacy signed sheet), an "archived
 *  offline" tombstone, and — for any closed return — an on-the-fly
 *  "Download report PDF" that needs no stored file at all. */
function DocsStrip({
  booking, docs, report, canDelete, onDownload, onDelete, onRenderPdf,
}: {
  booking: EquipmentBooking;
  docs: EquipmentDocument[];
  report: EquipmentReturnReport | null;
  canDelete: boolean;
  onDownload: (d: EquipmentDocument) => void;
  onDelete?: (d: EquipmentDocument) => void;
  onRenderPdf?: () => void;
}): JSX.Element | null {
  const live = docs.filter((d) => !d.archived_at);
  const archived = docs.filter((d) => d.archived_at);
  const hasStoredReport = live.some((d) => d.kind === "return_report");
  const showRender = !!onRenderPdf && !hasStoredReport && booking.status === "RETURNED" && !!report && report.status === "CONFIRMED";
  if (live.length === 0 && archived.length === 0 && !showRender) return null;
  return (
    <div class="eq-docs">
      {live.map((d) => (
        <span key={d.id} class="eq-doc-wrap">
          <button
            type="button"
            class="eq-doc"
            onClick={() => onDownload(d)}
            title={`${d.file_name} · ${formatBytes(d.size_bytes)} · stored ${d.created_at.slice(0, 10)} by ${d.uploaded_by || "—"} · open`}
          >
            {d.kind === "return_report" ? "📄" : "📎"} <span class="eq-doc-name">{d.kind === "return_report" ? "Return report (PDF)" : d.file_name}</span>
            <span class="eq-doc-meta">{formatBytes(d.size_bytes)} · {d.created_at.slice(0, 10)}</span>
          </button>
          {canDelete && onDelete && (
            <button
              type="button"
              class="eq-doc-del"
              onClick={() => onDelete(d)}
              aria-label={`Delete ${d.file_name}`}
              title="Delete this document"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {archived.map((d) => (
        <span key={d.id} class="eq-doc eq-doc-archived" title={`${d.file_name} — downloaded and removed from storage on ${d.archived_at?.slice(0, 10)} by ${d.archived_by || "an admin"}`}>
          🗄 <span class="eq-doc-name">Report archived offline</span>
          <span class="eq-doc-meta">{d.archived_at?.slice(0, 10)}</span>
        </span>
      ))}
      {showRender && (
        <button type="button" class="eq-action eq-action-quiet" onClick={onRenderPdf} title="Render the return report PDF from the register (no stored file needed)">
          ⬇ Download report PDF
        </button>
      )}
    </div>
  );
}
